import { Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';

// RxJS
import { interval, Subject, takeUntil } from 'rxjs';

// Services
import { StateService } from '@modules/settings/services/state.service';

// Types
import { StreamStatus } from '../../types/stream-status';
import { io, Socket } from 'socket.io-client';
import { Record } from '@modules/records/types/record';

// Env
import { environment } from '@environment';

// Cinedeck Web Preview
declare var CinedeckPreview: any;

@Component({
    selector: 'app-preview',
    templateUrl: './preview.component.html',
    styleUrl: './preview.component.less',
    standalone: false
})
export class PreviewComponent implements OnInit, OnChanges, OnDestroy {

  // ViewChild
  @ViewChild('webPreview', { static: true }) webPreviewElement?: ElementRef<HTMLElement>;
  @ViewChild('timecode', { static: false }) timecodeRef?: ElementRef<HTMLElement>;

  // Inputs
  @Input() record?: Record;
  @Input() status?: StreamStatus | null | undefined;
  @Input() channel?: number;
  @Input() audio = false;
  @Input() audioPair = 0;
  @Input() volume = 1;
  @Input() serverId?: string;

  // Outputs
  @Output() timecodeChanged = new EventEmitter<any>();
  @Output() ccChanged = new EventEmitter<any>();
  @Output() audioLevels = new EventEmitter<Array<{ peak: number, rm: number }>>();

  // Public
  public preview: any;
  public aspectRatio: string | null | undefined;
  public timecode?: string; // 'gen' | 'sdi' | 'ndi' | 'srt' | 'extltc' | 'adrienne' | 'trigger_timer' | 'rs422' | 'playback' | 'playlist' | 'irig_systime' | 'irig_vanc' | 'irig_clip' |

  // Private
  private frameSize = { width: 0, height: 0 };
  private alive = new Subject<void>();
  private accessToken?: string;
  private socket?: Socket;
  private socketAudio: any;
  private info = {
    url: '',
    audioUrl: '',
    audio: false
  };

  /**
   * Constructor
   */

  constructor(
    private ngZone: NgZone,
    private stateService: StateService,
  ) {
  }

  /**
   * Component lifecycle
   */

  ngOnInit(): void {

    // Check for resize canvas
    interval(1000)
      .pipe(takeUntil(this.alive))
      .subscribe(() => this.checkForCanvasSizeChange());

    // Token
    this.stateService.getState()
      .pipe(takeUntil(this.alive))
      .subscribe(state => {
        this.accessToken = state?.accessToken;
        // Setup preview
        this.setupPreview();
      });

    setTimeout(() => this.setupPreview(), 1000);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('channel' in changes || 'record'  in changes || 'serverId' in changes || 'audio' in changes || 'audioPair' in changes) {
      this.setupPreview();
    }
  }

  ngOnDestroy(): void {
    this.preview?.destroy();
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Methods
   */

  setupPreview(): void {
    // console.log(`[setupPreview]\n record = ${this.record}\n accessToken = ${this.accessToken}\nchannel = ${this.channel}\nserverId = ${this.serverId}\nconnected = ${this.socket?.connected}\n\n`);
    if (!this.record?.id || !this.accessToken || this.channel === undefined || !this.serverId) {
      return;
    }

    // Set URL
    const address = (environment.ssl ? 'wss' : 'ws') + '://' + environment.api;
    const url = `${address}/records/cinedeck/preview?id=${this.record?.id}&token=${this.accessToken}&serverId=${this.serverId}&channel=${this.channel}`;
    const audioUrl = `${address}/records/cinedeck/preview/audio?pair=${this.audioPair}&id=${this.record?.id}&token=${this.accessToken}&serverId=${this.serverId}&channel=${this.channel}`;
    // console.log(`[Preview][Params]\nrecord = ${this.record?.id}\nserverId = ${this.serverId}\nchannel = ${this.channel}\naudio = ${this.audio}\naudioPair = ${this.audioPair}`);
    // console.log('[Preview][Url] ', url);
    // console.log('[Preview][audioUrl] ', audioUrl);

    // Skip if same URL
    if (this.info.url === url && this.info.audioUrl === audioUrl && this.info.audio === this.audio && this.socket?.connected) {
      return;
    }
    this.info.url = url;
    this.info.audioUrl = audioUrl;
    this.info.audio = !!this.audio;

    // Create Preview
    this.ngZone.runOutsideAngular(() => {
      if (this.preview) {
        this.preview?.destroy();
        while (this.webPreviewElement?.nativeElement?.firstChild) {
          const childNode = this.webPreviewElement.nativeElement.lastChild;
          if (childNode) {
            this.webPreviewElement.nativeElement.removeChild(childNode);
          }
        }
      }

      // Create SocketIO
      if (this.socket) {
        this.socket.disconnect();
      }
      if (this.socketAudio) {
        this.socketAudio.disconnect();
      }
      
      // console.log('[Preview][URL] ', url);
      this.socket = io(url, {transports: ['websocket']});
      // this.socket?.on('connect', () => console.log('[Preview][SocketIO] Connected to WebSocket server'));
      // this.socket?.on('error', (err: any) => console.error('[Preview][SocketIO] Error:', err));
      // this.socket?.on('disconnect', () => console.log('[Preview][SocketIO] Disconnected from server'));

      this.socketAudio = io(audioUrl, {transports: ['websocket']});
      // this.socketAudio?.on('connect', () => console.log('[Audio][SocketIO] Connected to WebSocket server'));
      // this.socketAudio?.on('error', (err: any) => console.error('[Audio][SocketIO] Error:', err));
      // this.socketAudio?.on('disconnect', () => console.log('[Audio][SocketIO] Disconnected from server'));

      // Create Preview
      this.preview = new CinedeckPreview({
        videoTagNode: this.webPreviewElement?.nativeElement,
        debug: false,
        audio: this.audio,
        audioPair: this.audioPair,
        socketURL: url,
        socketAudioURL: audioUrl,
        socket: this.socket,
        socketAudio: this.socketAudio
      });
      this.updateVolume(this.volume);
      // this.preview.play();
      this.preview.addTimecodeLisener((data: any) => {
        this.ngZone.runOutsideAngular(() => {
          // Old CC
          if (data && data.cc) {
            const cc = data.cc;
            this.ccChanged.emit(cc);
          }
          // New CC
          if (data && data.captions) {
            const cc = data.captions;
            this.ccChanged.emit(cc);
          }
          // Set Timecode
          if (data && data.timecodes) {
            const timecodes = data.timecodes;
            this.timecodeChanged.emit(timecodes);
            if (this.timecodeRef) {
              this.timecodeRef.nativeElement.innerHTML = timecodes.playback || (this.timecode ? timecodes[this.timecode] : '00:00:00:00');
            }
          }
          // Audio levels
          if (data && data.audioLevels && data.audioLevels.peaks && data.audioLevels.rms) {
            const audioLevels = [];
            for (let index = 0; index < data.audioLevels.peaks.length; index++) {
              audioLevels.push({peak: data.audioLevels.peaks[index], rm: data.audioLevels.rms[index]})
            }
            this.audioLevels.emit(audioLevels);
          }
        });
      });
    });

    setTimeout(() => this.zoomFit(), 100);
  }

  updateVolume(value: number): void {
    this.volume = value;
    if (this.preview) {
      this.preview.setVolume(this.volume);
    }
  }

  private getAspectRatioValue(aspect: string | null | undefined): number {
    switch (aspect) {
      case '1': return 1;
      case '0.9091': return 10/11;
      case '1.109': return 59/54;
      case '4:3': return 4/3;
      case '1.2121': return 40/33;
      case '1.4587': return 118/81;
      case '1.5': return 15/10;
      case '2': return 2;
      case '16:9': return 16/9;
      case '2.35': return 2.35;
      case 'auto':
      default:
        return -1;
    }
  }

  checkForCanvasSizeChange(): void {
    const canvas = this.preview?.canvas;
    if (!canvas) {
      return;
    }
    const frame = {width: +canvas.getAttribute('width'), height: +canvas.getAttribute('height')};
    // Update canvas style
    if (this.frameSize.width !== frame.width || this.frameSize.height !== frame.height) {
      this.frameSize.width = frame.width;
      this.frameSize.height = frame.height;
      this.zoomFit();
    }
  }

  zoomFit(): void {
    const canvas = this.preview?.canvas;
    if (!canvas) {
      return;
    }
    canvas.style.display = 'block';
    canvas.style.margin = 'auto';
    canvas.style.width = 'auto';
    canvas.style.height = 'auto';
    canvas.style.minWidth = 'auto';
    canvas.style.minHeight = 'auto';
    canvas.style.maxWidth = '100%';
    canvas.style.maxHeight = '100%';
    let widthRation = this.frameSize.width
      ? canvas.offsetParent?.clientWidth / this.frameSize.width
      : 0;
    let heightRation = this.frameSize.height
      ? canvas.offsetParent?.clientHeight / this.frameSize.height
      : 0;
    if (widthRation > 1 || heightRation > 1) {
      if (widthRation < heightRation) {
        canvas.style.width = '100%';
      } else {
        canvas.style.height = '100%';
      }
    }
    // Aspect ration
    const aspectRationValue = this.getAspectRatioValue(this.aspectRatio);
    if (this.aspectRatio && aspectRationValue > 0) {
      if (widthRation > 1 || heightRation > 1) {
        if (widthRation < heightRation) {
          const scaleY = (this.frameSize.width / aspectRationValue) / this.frameSize.height;
          canvas.style.transform = `scale(1, ${scaleY})`;
        } else {
          const scaleX = (this.frameSize.height * aspectRationValue) / this.frameSize.width;
          canvas.style.transform = `scale(${scaleX}, 1)`;
        }
      }
    } else {
      canvas.style.transform = 'scale(1, 1)';
    }
  }

}
